import type { UserRole } from '@/lib/constants/roles';
import { type WidgetId } from '../config';
import { useDashboardLayout } from '../use-dashboard-layout';
import { WidgetGrid } from './WidgetGrid';

interface Props {
  role: UserRole;
  editing: boolean;
}

/**
 * Persistent KPI strip above the tabs. Uses the same sortable +
 * resizable grid as every tab; its layout is stored under the `kpi`
 * scope so KPIs and tab widgets persist independently.
 */
export function KpiStrip({ role, editing }: Props) {
  const { layout, reorder, setSize, remove } = useDashboardLayout('kpi');
  if (layout.length === 0) return null;
  /*
   * `autoFit` so the KPI tiles flow into as many columns as fit. The
   * 12-col fixed grid was making tiles too narrow at 1366px laptops
   * (lg breakpoint = 1024px+, each col = ~110px, so a 2-col-span tile
   * was ~220px, not enough for 9-digit counts like "20,821,184").
   * minTilePx=200 keeps every tile readable at any viewport width.
   */
  return (
    <WidgetGrid
      layout={layout}
      editing={editing}
      onReorder={reorder}
      onResize={(id: WidgetId, size) => setSize(id, size)}
      onRemove={remove}
      role={role}
      autoFit
      minTilePx={200}
    />
  );
}
